import {
  Button,
  Center,
  CenterProps,
  HStack,
  Icon,
  IconButton,
  Select,
  SkeletonText,
  Spinner,
  Square,
  Stack,
  Text,
  useToast,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { convertFileToBase64 } from '@pelicargo/ui';
import { getPrettyDocumentType, modelUtils } from '@pelicargo/utils';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { HiUpload } from 'react-icons/hi';
import { HiOutlineDocument, HiXMark } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { useDocumentViewer } from '../hooks/documents/useDocumentViewer';
import { useFormDocuments } from '../hooks/documents/useFormDocuments';

type Props = CenterProps & {
  name?: string;
  documentType?: DocumentType;
  requestId?: number;
  isTypeSelectable?: boolean;
  maxFiles?: number;
  useLocalDocuments?: boolean;
  onDocumentsChange?: (documents: Partial<Document>[]) => void;
};

export const SimpleDropzone = ({
  name,
  documentType,
  requestId,
  isTypeSelectable = false,
  maxFiles = 10,
  useLocalDocuments = false,
  onDocumentsChange,
  ...rest
}: Props) => {
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const [filesDropped, setFilesDropped] = useState(1);
  const [requestDocuments] = useFormDocuments({
    requestId: useLocalDocuments ? undefined : requestId,
    formFieldName: useLocalDocuments ? undefined : name,
  });
  const [isDocumentDeleting, setIsDocumentDeleting] = useState<Record<number, boolean>>({});
  const [documentTypes, setDocumentTypes] = useState<Record<number, DocumentType>>({});

  const { setValue, register, watch } = useFormContext();
  if (name) register(name);

  const localDocuments = name ? watch(name) || [] : [];
  const documents = useLocalDocuments ? localDocuments : requestDocuments;

  const { mutateAsync: uploadDocument, isLoading: isUploading } = trpc.uploadDocument.useMutation();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();
  const viewer = useDocumentViewer();

  /**
   * This is the editing section, so it lists the viewer's own uploads and nothing else — the
   * counterparty's paperwork is already visible (and downloadable) in the grouped list above, and
   * showing it here again only offered controls that don't apply to it.
   *
   * Filtering the rendered list, not the form field: the form is what feeds the grouped list, and
   * that one is meant to show the whole document set.
   */
  const ownedDocuments = useMemo(
    () => (documents ?? []).filter((document) => modelUtils.document.canManage(document, viewer)),
    [documents, viewer],
  );

  useEffect(() => {
    const documentTypeEntries = Object.entries(documentTypes);
    if (documentTypeEntries.length) {
      documentTypeEntries.forEach(([id, type]) => {
        const docIndex = documents.findIndex((doc) => doc.id === Number(id));
        if (docIndex !== -1) {
          if (name) setValue(`${name}.${docIndex}`, { ...documents[docIndex], type } as any);
        }
      });
    }
    onDocumentsChange?.(documents);
  }, [documentTypes, documents, name, onDocumentsChange, setValue]);

  const onDrop = async (files) => {
    // Counted against the viewer's own uploads: the counterparty's documents aren't theirs to
    // manage, so they shouldn't eat into the allowance either.
    const isReplacingSingleFile = maxFiles === 1 && ownedDocuments.length >= maxFiles;

    if (!isReplacingSingleFile && (ownedDocuments.length >= maxFiles || files.length > maxFiles)) {
      toast({ title: 'Error', description: `You can only upload up to ${maxFiles} files.`, status: 'error' });
      return;
    }
    setFilesDropped(files.length);

    const existingDocuments = name ? watch(name) || [] : [];
    const newDocumentPromises = files.map(uploadFile);
    const newDocuments = (await Promise.all(newDocumentPromises)).filter(Boolean);

    if (name) {
      const nextDocuments = maxFiles === 1 ? newDocuments : [...(existingDocuments || []), ...newDocuments];
      setValue(name, nextDocuments);
    }

    setFilesDropped((prev) => Math.max(1, prev - newDocuments.length));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > 5) throw new Error('File size exceeds 5MB. Please choose a smaller file.');

        const newDocument = await uploadDocument({
          content: await convertFileToBase64(file),
          filename: file.name,
          content_type: file.type,
          document_type: documentType,
          request_id: requestId,
        });

        if (!newDocument?.id) throw new Error('Something went wrong. Please try again.');

        return newDocument;
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      }
    },
    [uploadDocument, documentType, requestId, toast],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: true }));
        await deleteDocument({ id });

        if (name) {
          const nextDocuments = (watch(name) || []).filter((doc) => doc?.id !== id);
          setValue(name, nextDocuments);
        }
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      } finally {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: false }));
      }
    },
    [deleteDocument, name, setValue, toast, watch],
  );

  const inputProps = getInputProps();

  const renderItem = (item: Partial<Document>, index: number) => {
    const value = documentTypes[item?.id!] || item?.type;
    const isInvalidType = isTypeSelectable && value == null;

    return (
      <Stack
        direction="row"
        align="center"
        justify="space-between"
        w="full"
        borderWidth="1px"
        pl="4"
        py="2"
        borderRadius="md"
        key={index}
      >
        <Stack direction="row" align="center" minW={0} flex="1">
          <Icon as={HiOutlineDocument} flexShrink={0} />
          <SkeletonText minW="10" noOfLines={1} isLoaded={!!item?.id}>
            <Show if={isTypeSelectable}>
              <Select
                value={value}
                onChange={(event) => {
                  setDocumentTypes((prev) => ({
                    ...prev,
                    [item?.id!]: event.target.value as DocumentType,
                  }));
                }}
                borderWidth={isInvalidType ? '1px' : undefined}
                borderColor={isInvalidType ? 'red' : undefined}
              >
                <option key={null} value={null}>
                  {null}
                </option>
                {Object.values(DocumentType).map((type) => (
                  <option key={type} value={type}>
                    {getPrettyDocumentType(type)}
                  </option>
                ))}
              </Select>
              <Show if={isInvalidType}>
                <Text color="error" fontSize="xs">
                  Please select a document type.
                </Text>
              </Show>
            </Show>
            <Show if={!isTypeSelectable}>
              <Text w="fit-content">{getPrettyDocumentType(item?.type)}</Text>
            </Show>
          </SkeletonText>
          <SkeletonText minW={0} flex="1" noOfLines={1} isLoaded={!!item?.id}>
            <Text w="full" noOfLines={1} wordBreak="break-all" title={item?.original_filename}>
              {item?.original_filename}
            </Text>
          </SkeletonText>
        </Stack>
        <Stack direction="row" align="center" spacing="4">
          <IconButton
            p="0"
            variant="link"
            icon={<HiXMark />}
            isLoading={isDocumentDeleting[item?.id!]}
            aria-label="Remove document"
            onClick={() => handleDelete(item?.id)}
          />
        </Stack>
      </Stack>
    );
  };

  const copy = useMemo(() => {
    return ownedDocuments.length > 0 ? 'Click to replace' : 'Click to upload';
  }, [ownedDocuments.length]);

  return (
    <Stack>
      <Stack spacing="2" w="full">
        {ownedDocuments.map(renderItem)}
        <Show if={isUploading}>
          {Array.from({ length: filesDropped }).map((_, index) => renderItem({} as Document, index))}
        </Show>
      </Stack>
      <Center
        borderWidth="1px"
        borderColor="border.default"
        borderStyle="dashed"
        borderRadius="lg"
        px="6"
        py="4"
        flexDirection="column"
        w="full"
        {...getRootProps()}
        {...rest}
      >
        <Stack spacing="2" py="2" align="center">
          <VisuallyHiddenInput type="file" display="none" {...omit(inputProps, 'size')} />
          <Show if={!isUploading}>
            <Square size="10" borderRadius="lg">
              <Icon as={HiUpload} boxSize="5" />
            </Square>
          </Show>
          <Show if={isUploading}>
            <Square size="10" borderRadius="lg">
              <Spinner size="sm" />
            </Square>
          </Show>
          <Stack align="center" textAlign="center" spacing="1">
            <HStack spacing="1" align="center" whiteSpace="nowrap">
              <Button variant="link" colorScheme="secondary" size="md" p="0">
                {copy}
              </Button>
              <Text fontSize="md">or drag and drop</Text>
            </HStack>
            <Text fontSize="xs">PDFs are recommended.</Text>
          </Stack>
        </Stack>
      </Center>
    </Stack>
  );
};
